import { ENTITY, UserActions } from '@/enums';
import useCheckScopeCombinations from '@/hooks/useCheckScopeCombinations';
import { PRIVATE_ROUTES } from '@/routes';
import { CustomTabs, CustomTabsProps } from '@amili-v3/amili-v3-design';
import { FC, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';

export enum AnalysisTabPane {
  RESEARCH = 'research',
  COMMERCIAL = 'commercial',
}

const AnalysisTabs: FC<Omit<CustomTabsProps, 'items'>> = props => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { canGetCommercialAnalysisBatch: canReadCommercial } =
    useCheckScopeCombinations([
      [{ entity: ENTITY.COMMERCIAL_ANALYSIS_BATCH, action: UserActions.GET }],
    ]);
  const { canGetAnalysis: canReadResearch } = useCheckScopeCombinations([
    [{ entity: ENTITY.ANALYSIS, action: UserActions.GET }],
  ]);
  const analysisTabCollections = [
    {
      key: AnalysisTabPane.RESEARCH,
      permission: canReadResearch,
    },
    {
      key: AnalysisTabPane.COMMERCIAL,
      permission: canReadCommercial,
    },
  ];

  const handleTabChange = (activeKey: string) => {
    navigate(
      generatePath(PRIVATE_ROUTES.ANALYSIS.FULL_TAB_NAME, {
        tabName: activeKey,
      }),
    );
  };

  const analysisTabItems = analysisTabCollections.reduce(
    (accumulator, analysisTabKey) => {
      if (!analysisTabKey.permission) return accumulator;

      accumulator.push({
        key: analysisTabKey.key,
        label: t(`analysis.${analysisTabKey.key}`),
      });
      return accumulator;
    },
    [] as CustomTabsProps['items'],
  );

  return (
    <CustomTabs
      {...props}
      isShowTab={analysisTabItems.length > 1}
      items={analysisTabItems}
      size="large"
      destroyInactiveTabPane
      onChange={handleTabChange}
    />
  );
};

export default memo(AnalysisTabs);
